import { Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Grid, GridItem, Select, Input } from "@chakra-ui/react"
import { format } from "date-fns";

import { getCurrencySymbol } from "@/utils/currency";
import { CURRENCY, MONTHS_OPTION, PAYMENT_STATUS } from "@/common/constants";
import CurrencyInput from "../Fields/CurrencyInput";
import { COLORS } from "@/common/constants";
import { Form, Formik } from "formik";

export type SireRequestorPaymentDataConfirmation = {
    invoiceId: string, // internal UID
    invoiceNumber: string, // user-facing
    companyName: string,
    reviewMonth: number,
    reviewYear: number,
    invoiceDate: Date | string,
    costReview: number,
    totalReviews: number,
    currency: string, // "IDR" / "USD"
    totalAmount: number,
    downloadId?: string,
}

type ModalSireRequestorPaymentConfirmProps = {
    isOpen: boolean,
    onClose: () => void,
    handleCancel: () => void,
	handleConfirm: (approvalStatus: string) => Promise<void>
    dataConfirmation?: SireRequestorPaymentDataConfirmation,
    approvalStatus: string // of number
    handleChangeApprovalStatus: (status: string) => void, // string of number, can easily be converted to number
    handleChangeNote: (note: string) => void,
    errorMessageApproval: string,
    errorMessageNote: string,
    handleDownload: (downloadId: string) => void,
    amountIDR: number;
    handleChangeAmountIDR: (amount: number) => void;
}

const ModalSireRequestorPaymentConfirm = ({
    isOpen,
    onClose,
    handleCancel,
    handleConfirm,
    approvalStatus,
    dataConfirmation,
    handleChangeApprovalStatus,
    handleChangeNote,
    errorMessageApproval,
    errorMessageNote,
    handleDownload,
    amountIDR,
    handleChangeAmountIDR,
}: ModalSireRequestorPaymentConfirmProps) => {
    return (
        <Modal onClose={onClose} isOpen={isOpen} isCentered size='xl'>
            <ModalOverlay />
				<Formik
					initialValues={{ status: "" }}
					onSubmit={async (values) => {
						await handleConfirm(values.status)
						handleChangeApprovalStatus(values.status)
					}}>
					{formik => (
						<Form>

            <ModalContent borderRadius={15} padding={'10px'}>
                <ModalHeader>Payment Approval</ModalHeader>
                <ModalCloseButton onClick={handleCancel} _focus={{ outline: 'none', boxShadow: 'none' }} />
                <ModalBody marginTop={5}>
                    <Grid templateColumns='repeat(2, 1fr)' gap={6}>
                        <GridItem w='100%'>
                            <p style={{ fontWeight: 'bold' }}>Requestor Company</p>
                            <p>{dataConfirmation?.companyName ?? '-'}</p>
                        </GridItem>
                        <GridItem w='100%'>
                            <p style={{ fontWeight: 'bold' }}>Review Month</p>
                            <p>{dataConfirmation?.reviewMonth && dataConfirmation?.reviewYear ? (MONTHS_OPTION[dataConfirmation.reviewMonth] + ' ' + dataConfirmation.reviewYear) : '-'}</p>
                        </GridItem>
                        <GridItem w='100%'>
                            <p style={{ fontWeight: 'bold' }}>Invoice ID</p>
                            <p>{dataConfirmation?.invoiceNumber ?? '-'}</p>
                        </GridItem>
                        <GridItem w='100%'>
                            <p style={{ fontWeight: 'bold' }}>Invoice Date</p>
                            <p>{dataConfirmation?.invoiceDate ? format(dataConfirmation?.invoiceDate, 'dd-MM-yyyy') : '-'}</p>
                        </GridItem>
                        <GridItem w='100%'>
                            <p style={{ fontWeight: 'bold' }}>Cost per Review</p>
                            <p>{dataConfirmation?.costReview ? getCurrencySymbol(CURRENCY.USD.code, dataConfirmation.costReview) : '-'}</p>
                        </GridItem>
                        <GridItem w='100%'>
                            <p style={{ fontWeight: 'bold' }}>Total Reviews</p>
                            <p>{dataConfirmation?.totalReviews ?? '-'}</p>
                        </GridItem>
                        <GridItem w='100%'>
                            <p style={{ fontWeight: 'bold' }}>Total Amount</p>
                            <p>{dataConfirmation?.totalAmount ? getCurrencySymbol(CURRENCY.IDR.code, dataConfirmation.totalAmount) : '-'}</p>
                        </GridItem>
                        <GridItem w='100%'>
                        <p style={{ fontWeight: 'bold' }}>Total Amount in IDR</p>
                        <CurrencyInput
                            placeholder='Total Amount in IDR'
												prefix={CURRENCY.IDR.code}
                            value={amountIDR}
                            onValueChange={({ floatValue = 0 }) => handleChangeAmountIDR(floatValue)}
                            width="100%"
											/>
                        </GridItem>
                    </Grid>
                    {(dataConfirmation?.downloadId) && <div style={{ width: '100%', marginTop: 30, marginBottom: 20 }}>
                        <p style={{ fontWeight: 'bold', marginBottom: 5 }}>Proof of Payment</p>
                        <Button
                            onClick={() => {
												if (dataConfirmation?.downloadId) {
													handleDownload(dataConfirmation?.downloadId)
												}
                            }}
                            style={{ width: '100%' }}
										>Download</Button>
                    </div>}
									<Select name="status" fontSize="12px" cursor="pointer" marginTop={10} marginBottom={0} onChange={formik.handleChange} borderColor={errorMessageApproval ? 'red' : 'gray'}>
                        <option key='none' value={'-1'}>Please select...</option>
                        <option key='approved' value={String(PAYMENT_STATUS.COMPLETED)}>Approve</option>
                        <option key='rejected' value={String(PAYMENT_STATUS.REJECTED)}>Reject</option>
                    </Select>
                    {errorMessageApproval && <p style={{ fontSize: '14px', color: 'red' }}>{errorMessageApproval}</p>}
                    {approvalStatus === String(PAYMENT_STATUS.REJECTED) && <>
                        <p style={{ marginTop: 5, marginBottom: 5, fontWeight: 'bold' }}>Note</p>
                        <Input placeholder="Input your note here" onChange={(e) => handleChangeNote(e.target.value)} borderColor={errorMessageNote ? 'red' : 'gray'} />
                        {errorMessageNote && <p style={{ fontSize: '14px', color: 'red' }}>{errorMessageNote}</p>}
                    </>}
                </ModalBody>
                <ModalFooter style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: 10 }}>
									<Button type="button" disabled={formik.isSubmitting} onClick={handleCancel} borderColor='#D0D5DD' bgColor='white'>Cancel</Button>
									<Button type="submit" isLoading={formik.isSubmitting} disabled={amountIDR <= 0 || formik.isSubmitting} loadingText="Confirming" bgColor={COLORS.BUTTON.BACKGROUND} color={COLORS.BUTTON.FOREGROUND}>Confirm</Button>
                </ModalFooter>
            </ModalContent>
						</Form>
					)}
				</Formik>
        </Modal>
    )
}

export default ModalSireRequestorPaymentConfirm;